/**
 * Resolve an arbitrary language tag (browser `pt-PT`, cookie `pt_br`, admin
 * `PT-BR`, …) against a list of our locale codes:
 *  1. exact match on the underscore form (`pt-BR` and `pt_BR` both reach `pt_br`);
 *  2. the base subtag (`en-US` -> `en`, `ar-EG` -> `ar`);
 *  3. a regional code of ours with the same base: our Portuguese copy is
 *     Brazilian and filed as `pt_br`, so a browser asking for plain `pt` or
 *     `pt-PT` gets it instead of English.
 *
 * Import-free on purpose: the SSR-safe API helpers (features/shared/locale.ts)
 * use it without pulling in the i18n bundle. HTML_DIR_SCRIPT in config.ts
 * inlines the same three steps — keep the two in sync.
 */
export const resolveLanguageCode = <T extends string>(
    value: string | null | undefined,
    allowed: readonly T[]
): T | null => {
    if (!value) return null;
    const lower = value.trim().toLowerCase();
    if (!lower) return null;
    const exact = lower.replace(/-/g, "_");
    const base = exact.split("_")[0];
    return (
        allowed.find((code) => code === exact) ??
        allowed.find((code) => code === base) ??
        allowed.find((code) => code.startsWith(`${base}_`)) ??
        null
    );
};
